export function dpr() {
    return window.devicePixelRatio || 1;
}
/**
 * Загружает и декодирует изображение с оптимизациями для производительности.
 *
 * Особенности:
 * - Использует img.decode() для асинхронного декодирования перед использованием (с fallback)
 * - Устанавливает src последним для поддержки Safari (избегает множественных запросов)
 * - Проверяет image.complete после установки src для предотвращения пропуска события
 * - Поддерживает crossOrigin для CORS-запросов
 *
 * @param src - URL изображения для загрузки
 * @param crossOrigin - режим CORS (по умолчанию 'anonymous')
 * @returns Promise с HTMLImageElement после успешной загрузки и декодирования
 */
export function loadImageFromSrc(src, crossOrigin = 'anonymous') {
    return new Promise((resolve, reject) => {
        const img = new Image();
        let isResolved = false; // Защита от двойного вызова resolve
        const safeResolve = (image) => {
            if (isResolved)
                return;
            isResolved = true;
            resolve(image);
        };
        const safeReject = (error) => {
            if (isResolved)
                return;
            isResolved = true;
            reject(error);
        };
        // Устанавливаем crossOrigin ПЕРЕД src (важно для Safari)
        img.crossOrigin = crossOrigin;
        // Регистрируем обработчики событий ПЕРЕД установкой src
        img.onload = () => {
            // Проверяем, поддерживается ли decode()
            // Ссылка: https://stackoverflow.com/questions/39777833/image-onload-event-in-isomorphic-universal-react-register-event-after-image-is
            if (typeof img.decode === 'function') {
                // Используем decode() для асинхронного декодирования
                img
                    .decode()
                    .then(() => safeResolve(img))
                    .catch((error) => {
                    // Если decode() не удался, но изображение загружено, все равно разрешаем промис
                    // Это может произойти для некоторых форматов или в старых браузерах
                    console.warn('Image decode() failed, but image is loaded:', error);
                    safeResolve(img);
                });
            }
            else {
                // Fallback для браузеров без поддержки decode()
                safeResolve(img);
            }
        };
        img.onerror = () => safeReject(new Error('Image load failed'));
        // Устанавливаем src ПОСЛЕДНИМ для поддержки Safari
        // Это предотвращает множественные запросы при обновлении других атрибутов (sizes, srcSet)
        // Ссылка: https://github.com/vercel/next.js/blob/canary/packages/next/src/client/image-component.tsx
        img.src = src;
        // Проверяем, не загружено ли изображение уже (например, из кэша браузера)
        // Это нужно делать ПОСЛЕ установки src
        // Ссылка: https://stackoverflow.com/questions/39777833/image-onload-event-in-isomorphic-universal-react-register-event-after-image-is
        if (img.complete) {
            // Изображение уже загружено (из кэша браузера)
            // Очищаем onload, чтобы предотвратить двойной вызов
            img.onload = null;
            // Проверяем, поддерживается ли decode()
            if (typeof img.decode === 'function') {
                img
                    .decode()
                    .then(() => safeResolve(img))
                    .catch((error) => {
                    // Если decode() не удался, но изображение загружено, все равно разрешаем промис
                    console.warn('Image decode() failed, but image is loaded:', error);
                    safeResolve(img);
                });
            }
            else {
                // Fallback для браузеров без поддержки decode()
                safeResolve(img);
            }
        }
    });
}
export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.9) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob)
                return reject(new Error('toBlob failed'));
            resolve(blob);
        }, type, quality);
    });
}
export function blobToFile(blob, name) {
    return new File([blob], name, { type: blob.type, lastModified: Date.now() });
}
export function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
}
export function constrainRect(rect, bounds) {
    const x = clamp(rect.x, bounds.x, bounds.x + bounds.w - rect.w);
    const y = clamp(rect.y, bounds.y, bounds.y + rect.h > bounds.y + bounds.h ? bounds.y + bounds.h - rect.h : rect.y);
    return Object.assign(Object.assign({}, rect), { x, y });
}
